import { captureException } from "@sentry/react";
import { getTokenAsync } from "./getAuthToken";
import { taskServiceBaseUrl } from "@coworker/app/src/core/cloud-services/cloud-services.helper";
import { isChineseEnvironment } from "@coworker/reusable/helpers/devices";
import { getEnvironmentId } from "@coworker/reusable/helpers/environment";
import packageJson from "../../../../package.json";
import { reportMessageToSentry } from "../useConfigureSentry";

export class HttpError extends Error {
  constructor(status, message) {
    super(message); // (1)
    this.status = status;
    this.name = "HttpError";
  }
}

const isChina = isChineseEnvironment();

export const TASKS_SERVICE_URL = taskServiceBaseUrl(
  getEnvironmentId(),
  isChina
);

const makeHeaders = (token, method) => ({
  Authorization: `Bearer ${token}`,
  ...(method.toUpperCase() !== "DELETE" && {
    "Content-Type": "application/json",
    "X-Client-Version": packageJson.version,
  }),
});

/***
 * @returns A promise which resolves when the request is done.
 * If you care about results then use the callback.
 */
export async function perform(
  method,
  url,
  bodyData,
  callback,
  responseType,
  additionalHeaders = {}
) {
  const token = await getTokenAsync();
  if (!token || typeof token !== "string") {
    return;
  }
  const params = {
    method,
    headers: { ...makeHeaders(token, method), ...additionalHeaders },
  };
  if ("GET" !== method && bodyData) params.body = JSON.stringify(bodyData);
  let result;
  try {
    const response = await fetch(url, params);
    if (!response.ok) {
      throw new HttpError(
        response.status,
        `HTTP error! Status: ${response.status}`
      );
    }
    // Handle response based on type
    if (responseType === "blob") {
      const blob = await response.blob();
      callback(blob); // Use callback with blob data
    } else {
      const text = await response.text();
      try {
        result = JSON.parse(text);
      } catch {
        console.log("perform: Can't parse", text);
      }
      if (result) callback(result);
    }
  } catch (error) {
    console.error("Fetch error:", error);
    captureException(error, {
      extra: { url, params, bodyData },
      tags: { module: "network" },
    });
  }
}

export async function fetchAPI(
  method,
  url,
  bodyData,
  responseType,
  additionalHeaders = {}
) {
  const token = await getTokenAsync();
  if (!token || typeof token !== "string") {
    throw new Error("Invalid token");
  }
  const params = {
    method,
    headers: { ...makeHeaders(token, method), ...additionalHeaders },
  };

  if (method !== "GET" && bodyData) params.body = JSON.stringify(bodyData);

  const response = await fetch(url, params);

  if (!response.ok) {
    throw new HttpError(response.status, response.statusText);
  }

  if (responseType === "blob") {
    const blob = await response.blob();
    return { blob, status: response.status };
  } else {
    const result = await response.json();
    return { result, status: response.status };
  }
}

export async function postFileType(url = "", body) {
  const token = await getTokenAsync();
  if (!token || typeof token !== "string") {
    throw new Error("No token found");
  }
  const params = {
    method: "POST",
    body,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const response = await fetch(url, params);
  if (!response.ok) throw new Error("Could not upload image");
  return JSON.parse(await response.text());
}
